import { StyledTableColumn } from "@/components/styled/table/StyledTable";
import { StudySessionProgressEnum, StudySessionType } from "@knowt/syncing/graphql/schema";
import { ClassMemberSortByKeys } from "./SetProgressStatsTable";
import { FlexColumn } from "@/components/Flex";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { getBackgroundColor, getColor } from "@/utils/progressPill";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import {
    formatTimeStudying,
    getFormattedCountdown,
    TIME_SECONDS,
    timeDeltaFromNow,
} from "@knowt/syncing/utils/dateTimeUtils";
import { TimerIcon, TimerOffIcon } from "lucide-react";
import { iconSizes } from "@/utils/iconProps";
import dayjs from "dayjs";
import Mountable from "@/components/styled/div/Mountable";
import HoverPopper from "@/components/HoverPopper";
import noop from "@knowt/syncing/utils/noop";
import { borderRadius } from "@/utils/borderRadius";
import {
    DEFAULT_STATS,
    NoteStudyAnalyticsForUser,
    SetStudyAnalyticsForUser,
} from "@knowt/syncing/hooks/study/statsUtils";
import TestModeColumns from "../TestRelatedTableColumns";

const ColumnsPerSelectedStudyMode = ({
    studyMode,
    usersStudyAnalytics,
    onOpenTestPopup,
    onStudyModeChange,
}: {
    studyMode: StudySessionType | null;
    usersStudyAnalytics?: Record<string, SetStudyAnalyticsForUser> | null;
    onOpenTestPopup: (userId: string | null) => void;
    onStudyModeChange: (mode: StudySessionType) => void;
}) => {
    switch (studyMode) {
        case StudySessionType.MATCH:
        case StudySessionType.SPACED:
        case StudySessionType.LEARN:
            return <ColumnsForLearnMatchSpacedMode usersStudyAnalytics={usersStudyAnalytics} mode={studyMode} />;
        case StudySessionType.TEST:
            return (
                <TestModeColumns
                    usersStudyAnalytics={Object.keys(usersStudyAnalytics || {}).reduce(
                        (acc, curr) =>
                            // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
                            ({ ...acc, [curr]: usersStudyAnalytics?.[curr]?.statsPerMode?.TEST }) as Record<
                                string,
                                NoteStudyAnalyticsForUser
                            >,
                        {} as Record<string, NoteStudyAnalyticsForUser>
                    )}
                    onOpenTestPopup={onOpenTestPopup}
                />
            );
        default:
            return (
                <AllStudyModeColumns usersStudyAnalytics={usersStudyAnalytics} onStudyModeChange={onStudyModeChange} />
            );
    }
};

export default ColumnsPerSelectedStudyMode;

const ProgressButton = ({
    onClick,
    mode,
    analytics,
}: { onClick?: () => void; mode: StudySessionType; analytics?: SetStudyAnalyticsForUser }) => {
    const { completed, lastActive, timeSpent, cardsSeen, averageGrade, lastSubmission, testHistory } = {
        ...DEFAULT_STATS,
        ...analytics?.statsPerMode?.[mode],
    } as typeof DEFAULT_STATS;

    return (
        <HoverPopper
            key={mode}
            PopperChildren={
                <FlexColumn
                    style={{
                        marginTop: spacing.XS,
                        borderRadius: borderRadius.smallModal,
                        backgroundColor: themeColors.neutralWhite,
                        gap: spacing.XS,
                        fontSize: "1.5rem",
                        fontWeight: "600",
                        padding: "1.6rem",
                        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.12)",
                        border: `1.5px dashed ${themeColors.neutral2}`,
                    }}>
                    <Mountable mount={completed !== undefined}>
                        <div>Completed: {completed ? "Yes" : "No"}</div>
                    </Mountable>
                    <Mountable mount={lastActive > 0}>
                        <div>Last Active: {lastActive ? `${timeDeltaFromNow(lastActive)} ago` : "N/A"}</div>
                    </Mountable>
                    <Mountable mount={timeSpent > 0}>
                        <div>Time Spent: {formatTimeStudying(timeSpent)}</div>
                    </Mountable>
                    <Mountable mount={cardsSeen != null}>
                        <div>Cards Seen: {cardsSeen}</div>
                    </Mountable>
                    <Mountable mount={averageGrade != null}>
                        <div>Average Grade: {averageGrade}</div>
                    </Mountable>
                    <Mountable mount={lastSubmission != null}>
                        <div>Last Submission: {dayjs((lastSubmission || 0) * 1000).format("MMM DD, YYYY")}</div>
                    </Mountable>
                    <Mountable mount={testHistory.length > 0}>
                        <div>Test History: {testHistory.length}</div>
                    </Mountable>
                </FlexColumn>
            }>
            {({ ref, onPointerEnter, onPointerLeave }) => (
                <CircularRectTextButton
                    ref={ref}
                    onPointerEnter={timeSpent > 0 ? onPointerEnter : noop}
                    onPointerLeave={timeSpent > 0 ? onPointerLeave : noop}
                    onClick={onClick}
                    className="secondaryTextBold1"
                    style={{
                        width: "fit-content",
                        padding: `${spacing.XS} ${spacing.MD_S}`,
                        backgroundColor: timeSpent
                            ? getBackgroundColor(StudySessionProgressEnum.NEW)
                            : themeColors.background,
                        gap: spacing.XXS,
                    }}>
                    {timeSpent ? <TimerIcon size={iconSizes.SM_S} /> : <TimerOffIcon size={iconSizes.SM_S} />}
                    <span
                        style={{
                            whiteSpace: "nowrap",
                        }}>
                        {timeSpent
                            ? getFormattedCountdown({
                                  days: Math.floor(timeSpent / TIME_SECONDS.DAY).toString(),
                                  hours: Math.floor((timeSpent % TIME_SECONDS.DAY) / TIME_SECONDS.HOUR).toString(),
                                  minutes: Math.floor((timeSpent % TIME_SECONDS.HOUR) / TIME_SECONDS.MINUTE).toString(),
                                  seconds: Math.floor(
                                      (timeSpent % TIME_SECONDS.MINUTE) / TIME_SECONDS.SECOND
                                  ).toString(),
                              })
                            : "0 m"}
                    </span>
                </CircularRectTextButton>
            )}
        </HoverPopper>
    );
};

const AllStudyModeColumns = ({
    usersStudyAnalytics,
    onStudyModeChange,
}: {
    usersStudyAnalytics?: Record<string, SetStudyAnalyticsForUser> | null;
    onStudyModeChange: (mode: StudySessionType) => void;
}) => {
    return (
        <>
            <StyledTableColumn title={"Mastery"} sortKey={ClassMemberSortByKeys.MASTERY}>
                {(userId: string) => (
                    <FlexColumn>
                        <CircularRectTextButton
                            style={{
                                width: "fit-content",
                                color: getColor(StudySessionProgressEnum.NEW),
                                backgroundColor: getBackgroundColor(StudySessionProgressEnum.NEW),
                                padding: `${spacing.XS} ${spacing.XS_2}`,
                            }}
                            disabled>
                            {usersStudyAnalytics?.[userId]?.progressPercentage || 0} %
                        </CircularRectTextButton>
                    </FlexColumn>
                )}
            </StyledTableColumn>
            <StyledTableColumn title={"Learn"} sortKey={ClassMemberSortByKeys.LEARN}>
                {(userId: string) => (
                    <FlexColumn>
                        <ProgressButton
                            onClick={() => onStudyModeChange(StudySessionType.LEARN)}
                            mode={StudySessionType.LEARN}
                            analytics={usersStudyAnalytics?.[userId]}
                        />
                    </FlexColumn>
                )}
            </StyledTableColumn>

            <StyledTableColumn title={"Test"} sortKey={ClassMemberSortByKeys.PRACTICE_TEST}>
                {(userId: string) => (
                    <FlexColumn>
                        <ProgressButton
                            onClick={() => onStudyModeChange(StudySessionType.TEST)}
                            mode={StudySessionType.TEST}
                            analytics={usersStudyAnalytics?.[userId]}
                        />
                    </FlexColumn>
                )}
            </StyledTableColumn>

            <StyledTableColumn title={"Matching"} sortKey={ClassMemberSortByKeys.MATCHING}>
                {(userId: string) => (
                    <FlexColumn>
                        <ProgressButton
                            onClick={() => onStudyModeChange(StudySessionType.MATCH)}
                            mode={StudySessionType.MATCH}
                            analytics={usersStudyAnalytics?.[userId]}
                        />
                    </FlexColumn>
                )}
            </StyledTableColumn>

            <StyledTableColumn title={"Spaced"} sortKey={ClassMemberSortByKeys.SPACED_REPETITION}>
                {(userId: string) => (
                    <FlexColumn>
                        <ProgressButton
                            onClick={() => onStudyModeChange(StudySessionType.SPACED)}
                            mode={StudySessionType.SPACED}
                            analytics={usersStudyAnalytics?.[userId]}
                        />
                    </FlexColumn>
                )}
            </StyledTableColumn>
        </>
    );
};

const ColumnsForLearnMatchSpacedMode = ({
    usersStudyAnalytics,
    mode,
}: {
    usersStudyAnalytics?: Record<string, SetStudyAnalyticsForUser> | null;
    mode: StudySessionType.LEARN | StudySessionType.MATCH | StudySessionType.SPACED;
}) => {
    return (
        <>
            <StyledTableColumn title={"Started"} sortKey={ClassMemberSortByKeys.STARTED}>
                {(userId: string) => <ProgressButton mode={mode} analytics={usersStudyAnalytics?.[userId]} />}
            </StyledTableColumn>
            <StyledTableColumn title={"Cards Seen"} sortKey={ClassMemberSortByKeys.CARDS_SEEN}>
                {(userId: string) => <span>{usersStudyAnalytics?.[userId]?.statsPerMode?.[mode].cardsSeen || 0}</span>}
            </StyledTableColumn>
            <StyledTableColumn title={"Time Spent"} sortKey={ClassMemberSortByKeys.TIME_SPENT}>
                {(userId: string) => (
                    <span>
                        {formatTimeStudying(usersStudyAnalytics?.[userId]?.statsPerMode?.[mode].timeSpent ?? 0)}
                    </span>
                )}
            </StyledTableColumn>
            <StyledTableColumn title={"Last Active"} sortKey={ClassMemberSortByKeys.LAST_ACTIVE}>
                {(userId: string) => (
                    <span>{timeDeltaFromNow(usersStudyAnalytics?.[userId]?.statsPerMode?.[mode].lastActive ?? 0)}</span>
                )}
            </StyledTableColumn>
        </>
    );
};
